// Central color + label system for the Class Schedule dashboard, so status
// badges, calendar chips and the KPI accents all read as one consistent
// language across light backgrounds.
//
// Spec palette:
//   Blue        → Scheduled
//   Green       → Completed
//   Red         → Cancelled
//   Orange      → Rescheduled / Recurring accent
//   Purple      → Makeup
//   Gray        → Released / neutral
//   Deep amber  → Not Started (Batch 11 detector — visually separate from
//                 both cancelled-red and rescheduled-amber so admins can
//                 spot missed classes at a glance)

export type StatusKey =
  | "scheduled"
  | "completed"
  | "cancelled"
  | "rescheduled"
  | "makeup"
  | "released"
  | "not_started";

export interface StatusMeta {
  key: StatusKey;
  label: string;
  color: string; // solid accent
  bg: string; // soft background
  text: string; // readable-on-bg text
}

const META: Record<StatusKey, StatusMeta> = {
  scheduled: { key: "scheduled", label: "Scheduled", color: "#3b82f6", bg: "rgba(59,130,246,0.12)", text: "#1d4ed8" },
  completed: { key: "completed", label: "Completed", color: "#22c55e", bg: "rgba(34,197,94,0.14)", text: "#15803d" },
  cancelled: { key: "cancelled", label: "Cancelled", color: "#ef4444", bg: "rgba(239,68,68,0.12)", text: "#b91c1c" },
  rescheduled: { key: "rescheduled", label: "Rescheduled", color: "#f59e0b", bg: "rgba(245,158,11,0.14)", text: "#b45309" },
  makeup: { key: "makeup", label: "Makeup", color: "#8b5cf6", bg: "rgba(139,92,246,0.14)", text: "#6d28d9" },
  released: { key: "released", label: "Released", color: "#6b7280", bg: "rgba(107,114,128,0.14)", text: "#374151" },
  not_started: { key: "not_started", label: "Not Started", color: "#ea580c", bg: "rgba(234,88,12,0.14)", text: "#9a3412" },
};

/**
 * Resolve the display status for a row. A makeup session (by scheduleType) reads
 * as "Makeup" regardless of its underlying scheduled/completed status; everything
 * else maps from the occurrence status, defaulting to Scheduled.
 */
export const statusMetaFor = (row: any): StatusMeta => {
  const type = String(row?.scheduleType || "").toLowerCase();
  if (type === "makeup") return META.makeup;
  const s = String(row?.status || "scheduled").toLowerCase();
  return META[(s as StatusKey)] ?? META.scheduled;
};

export const RECURRING_ACCENT = "#f59e0b"; // orange, per spec
export const ONEOFF_ACCENT = "#b45309";

export const statusMetaByKey = (key: StatusKey): StatusMeta => META[key];
export default META;
